import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '../../../i18n/useTranslation';
import { booksellersManageService } from '../../../api/booksellersManageService';
import {
  AUTH_TYPE_LABELS,
  AuthenticationType,
  TTS_SERVICE_LEVEL_LABELS,
  TtsServiceLevel,
  type CreateBooksellerDto,
  type UpdateBooksellerDto,
} from '../../../types/manageBooksellers';
import OAuthLoginTab from './OAuthLoginTab';
import EntitlementTab from './EntitlementTab';

interface Props {
  /** 'new' to create, a GUID to edit, or null to hide */
  mode: 'new' | string | null;
  onClose: () => void;
}

type Tab = 'basics' | 'oauthLogin' | 'entitlement' | 'tts';

const emptyDraft: CreateBooksellerDto = {
  name: '',
  slug: '',
  displayName: '',
  isActive: true,
  authType: AuthenticationType.OIDC,
  priority: 100,
  ttsServiceLevel: TtsServiceLevel.None,
  maxTtsChunkSize: 5000,
};

const SLUG_PATTERN = /^[a-z0-9-]+$/;

export default function BooksellerEditDrawer({ mode, onClose }: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>('basics');
  const [draft, setDraft] = useState<CreateBooksellerDto>(emptyDraft);
  const [concurrencyStamp, setConcurrencyStamp] = useState<string>('');
  const [conflict, setConflict] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const isNew = mode === 'new';
  const isEdit = typeof mode === 'string' && mode !== 'new';
  const editId = isEdit ? (mode as string) : null;

  const query = useQuery({
    queryKey: ['manage-bookseller', editId],
    queryFn: () => booksellersManageService.get(editId!),
    enabled: editId !== null,
  });

  useEffect(() => {
    setErrorMessage(null);
    setConflict(false);
    if (isNew) {
      setDraft(emptyDraft);
      setConcurrencyStamp('');
      setTab('basics');
      return;
    }
    if (query.data) {
      setDraft({
        name: query.data.name,
        slug: query.data.slug,
        displayName: query.data.displayName,
        logoUrl: query.data.logoUrl,
        websiteUrl: query.data.websiteUrl,
        isActive: query.data.isActive,
        authType: query.data.authType,
        priority: query.data.priority,
        supportEmail: query.data.supportEmail,
        apiHealthCheckUrl: query.data.apiHealthCheckUrl,
        metadata: query.data.metadata,
        ttsServiceLevel: query.data.ttsServiceLevel,
        preferredTtsProvider: query.data.preferredTtsProvider,
        maleVoiceId: query.data.maleVoiceId,
        femaleVoiceId: query.data.femaleVoiceId,
        maxTtsChunkSize: query.data.maxTtsChunkSize,
        availableVoicesJson: query.data.availableVoicesJson,
      });
      setConcurrencyStamp(query.data.concurrencyStamp);
    }
  }, [isNew, query.data]);

  const invalidateLists = () => {
    qc.invalidateQueries({ queryKey: ['manage-booksellers'] });
    if (editId) qc.invalidateQueries({ queryKey: ['manage-bookseller', editId] });
  };

  const readError = (err: unknown): { code: string; message: string } => {
    const e = err as { response?: { data?: { error?: { code?: string; message?: string } } }; message?: string };
    const code = e?.response?.data?.error?.code ?? '';
    const message = e?.response?.data?.error?.message ?? e?.message ?? 'Failed to save bookseller';
    return { code, message };
  };

  const create = useMutation({
    mutationFn: () => booksellersManageService.create(draft),
    onSuccess: () => {
      invalidateLists();
      onClose();
    },
    onError: (err) => setErrorMessage(readError(err).message),
  });

  const save = useMutation({
    mutationFn: () => {
      const payload: UpdateBooksellerDto = { ...draft, concurrencyStamp };
      return booksellersManageService.update(editId!, payload);
    },
    onSuccess: () => {
      invalidateLists();
      onClose();
    },
    onError: (err) => {
      const { code, message } = readError(err);
      if (code.toLowerCase().includes('concurrency') || message.toLowerCase().includes('concurrency')) {
        setConflict(true);
      } else {
        setErrorMessage(message);
      }
    },
  });

  const reload = async () => {
    setConflict(false);
    setErrorMessage(null);
    await query.refetch();
  };

  if (mode === null) return null;

  const slugValid = isNew ? SLUG_PATTERN.test(draft.slug) : true;
  const canSave = draft.name.trim().length > 0
    && draft.displayName.trim().length > 0
    && draft.slug.trim().length > 0
    && slugValid;

  return (
    <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose}>
      <aside
        className="absolute right-0 top-0 h-full w-full sm:w-[560px] bg-white shadow-xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between border-b border-neutral-200 px-4 py-3">
          <h2 className="text-lg font-semibold">
            {isNew ? t('App.Admin.Booksellers.BooksellerEditDrawer.TitleNew') : (query.data?.name ?? t('App.Admin.Booksellers.BooksellerEditDrawer.LoadingTitle'))}
          </h2>
          <button type="button" onClick={onClose} className="text-neutral-500 hover:text-neutral-900" aria-label={t('App.Admin.Booksellers.BooksellerEditDrawer.CloseAriaLabel')}>×</button>
        </header>

        <nav className="flex border-b border-neutral-200">
          {([
            ['basics', t('App.Admin.Booksellers.BooksellerEditDrawer.TabBasics')],
            ['oauthLogin', t('App.Admin.Booksellers.BooksellerEditDrawer.TabOAuthLogin')],
            ['entitlement', t('App.Admin.Booksellers.BooksellerEditDrawer.TabEntitlement')],
            ['tts', t('App.Admin.Booksellers.BooksellerEditDrawer.TabTts')],
          ] as const).map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              disabled={(key === 'oauthLogin' || key === 'entitlement') && isNew}
              title={(key === 'oauthLogin' || key === 'entitlement') && isNew ? t('App.Admin.Booksellers.BooksellerEditDrawer.TabSaveFirstHint') : undefined}
              className={`px-4 py-2 text-sm ${tab === key ? 'border-b-2 border-primary-600 text-primary-600' : 'text-neutral-600 hover:bg-neutral-50'} disabled:opacity-40 disabled:cursor-not-allowed`}
            >
              {label}
            </button>
          ))}
        </nav>

        {conflict && (
          <div className="bg-amber-50 border-b border-amber-200 text-amber-900 text-sm px-4 py-2 flex items-center justify-between">
            <span>{t('App.Admin.Booksellers.BooksellerEditDrawer.ConflictBanner')}</span>
            <button type="button" onClick={reload} className="underline">{t('App.Admin.Booksellers.BooksellerEditDrawer.ConflictReload')}</button>
          </div>
        )}

        {errorMessage && (
          <div className="bg-red-50 border-b border-red-200 text-red-700 text-sm px-4 py-2">
            {errorMessage}
          </div>
        )}

        <div className="flex-1 overflow-auto p-4">
          {tab === 'basics' && (
            <div className="space-y-3">
              <Field label={t('App.Admin.Booksellers.BooksellerEditDrawer.FieldName')}>
                <input
                  className={textInput}
                  value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  maxLength={256}
                />
              </Field>
              <Field label={t('App.Admin.Booksellers.BooksellerEditDrawer.FieldSlug')} hint={t('App.Admin.Booksellers.BooksellerEditDrawer.SlugHint')}>
                <input
                  className={`${textInput} ${!slugValid ? 'border-red-400' : ''}`}
                  value={draft.slug}
                  disabled={isEdit}
                  onChange={(e) => setDraft({ ...draft, slug: e.target.value })}
                  maxLength={64}
                />
                {!slugValid && (
                  <p className="text-xs text-red-600 mt-1">{t('App.Admin.Booksellers.BooksellerEditDrawer.SlugValidationError')}</p>
                )}
              </Field>
              <Field label={t('App.Admin.Booksellers.BooksellerEditDrawer.FieldDisplayName')}>
                <input
                  className={textInput}
                  value={draft.displayName}
                  onChange={(e) => setDraft({ ...draft, displayName: e.target.value })}
                  maxLength={256}
                />
              </Field>
              <Field label={t('App.Admin.Booksellers.BooksellerEditDrawer.FieldLogoUrl')}>
                <input
                  className={textInput}
                  type="url"
                  value={draft.logoUrl ?? ''}
                  onChange={(e) => setDraft({ ...draft, logoUrl: e.target.value || undefined })}
                />
              </Field>
              <Field label={t('App.Admin.Booksellers.BooksellerEditDrawer.FieldWebsiteUrl')}>
                <input
                  className={textInput}
                  type="url"
                  value={draft.websiteUrl ?? ''}
                  onChange={(e) => setDraft({ ...draft, websiteUrl: e.target.value || undefined })}
                />
              </Field>
              <Field label={t('App.Admin.Booksellers.BooksellerEditDrawer.FieldSupportEmail')}>
                <input
                  className={textInput}
                  type="email"
                  value={draft.supportEmail ?? ''}
                  onChange={(e) => setDraft({ ...draft, supportEmail: e.target.value || undefined })}
                />
              </Field>
              <Field label={t('App.Admin.Booksellers.BooksellerEditDrawer.FieldApiHealthCheckUrl')}>
                <input
                  className={textInput}
                  type="url"
                  value={draft.apiHealthCheckUrl ?? ''}
                  onChange={(e) => setDraft({ ...draft, apiHealthCheckUrl: e.target.value || undefined })}
                />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label={t('App.Admin.Booksellers.BooksellerEditDrawer.FieldPriority')}>
                  <input
                    className={textInput}
                    type="number"
                    min={0}
                    value={draft.priority}
                    onChange={(e) => setDraft({ ...draft, priority: Number(e.target.value) || 0 })}
                  />
                </Field>
                <div className="flex items-end pb-1">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={draft.isActive}
                      onChange={(e) => setDraft({ ...draft, isActive: e.target.checked })}
                    />
                    {t('App.Admin.Booksellers.BooksellerEditDrawer.FieldActive')}
                  </label>
                </div>
              </div>
              <details className="border-t border-neutral-100 pt-3">
                <summary className="text-xs text-neutral-600 cursor-pointer select-none">{t('App.Admin.Booksellers.BooksellerEditDrawer.AdvancedSection')}</summary>
                <div className="mt-3">
                  <Field label={t('App.Admin.Booksellers.BooksellerEditDrawer.FieldMetadata')}>
                    <textarea
                      className={`${textInput} font-mono text-xs`}
                      rows={6}
                      value={draft.metadata ?? ''}
                      onChange={(e) => setDraft({ ...draft, metadata: e.target.value || undefined })}
                      maxLength={4000}
                    />
                  </Field>
                </div>
              </details>
              {editId && (
                <div className="border-t border-neutral-100 pt-3 flex flex-wrap gap-4 text-xs">
                  <a
                    href={`/admin/test-bookseller-auth?bookseller=${editId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 hover:underline"
                  >
                    {t('App.Admin.Booksellers.BooksellerEditDrawer.TestAuthLink')}
                  </a>
                  <a
                    href={`/admin/test-entitlement-api?bookseller=${editId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 hover:underline"
                  >
                    {t('App.Admin.Booksellers.BooksellerEditDrawer.TestEntitlementLink')}
                  </a>
                </div>
              )}
            </div>
          )}

          {tab === 'oauthLogin' && (
            <div className="space-y-4">
              <Field label={t('App.Admin.Booksellers.BooksellerEditDrawer.AuthTypeLabel')}>
                <select
                  className={textInput}
                  value={draft.authType}
                  onChange={(e) => setDraft({ ...draft, authType: Number(e.target.value) as AuthenticationType })}
                >
                  {Object.entries(AUTH_TYPE_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
                <p className="text-xs text-neutral-400 mt-1">
                  {t('App.Admin.Booksellers.BooksellerEditDrawer.AuthTypeHint')}
                </p>
              </Field>
              {editId ? (
                <OAuthLoginTab booksellerId={editId} authType={draft.authType} />
              ) : (
                <p className="text-xs text-neutral-500">{t('App.Admin.Booksellers.BooksellerEditDrawer.OAuthSaveFirstHint')}</p>
              )}
            </div>
          )}

          {tab === 'entitlement' && (
            <div className="space-y-4">
              {editId ? (
                <EntitlementTab booksellerId={editId} />
              ) : (
                <p className="text-xs text-neutral-500">{t('App.Admin.Booksellers.BooksellerEditDrawer.EntitlementSaveFirstHint')}</p>
              )}
            </div>
          )}

          {tab === 'tts' && (
            <div className="space-y-3">
              <Field label={t('App.Admin.Booksellers.BooksellerEditDrawer.TtsServiceLevelLabel')}>
                <select
                  className={textInput}
                  value={draft.ttsServiceLevel}
                  onChange={(e) => setDraft({ ...draft, ttsServiceLevel: Number(e.target.value) as TtsServiceLevel })}
                >
                  {Object.entries(TTS_SERVICE_LEVEL_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </Field>
              <Field label={t('App.Admin.Booksellers.BooksellerEditDrawer.FieldPreferredTtsProvider')}>
                <input
                  className={textInput}
                  value={draft.preferredTtsProvider ?? ''}
                  onChange={(e) => setDraft({ ...draft, preferredTtsProvider: e.target.value || undefined })}
                  placeholder="e.g. openai, azure"
                />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label={t('App.Admin.Booksellers.BooksellerEditDrawer.FieldMaleVoiceId')}>
                  <input
                    className={textInput}
                    value={draft.maleVoiceId ?? ''}
                    onChange={(e) => setDraft({ ...draft, maleVoiceId: e.target.value || undefined })}
                  />
                </Field>
                <Field label={t('App.Admin.Booksellers.BooksellerEditDrawer.FieldFemaleVoiceId')}>
                  <input
                    className={textInput}
                    value={draft.femaleVoiceId ?? ''}
                    onChange={(e) => setDraft({ ...draft, femaleVoiceId: e.target.value || undefined })}
                  />
                </Field>
              </div>
              <Field label={t('App.Admin.Booksellers.BooksellerEditDrawer.FieldMaxTtsChunkSize')}>
                <input
                  className={textInput}
                  type="number"
                  min={0}
                  value={draft.maxTtsChunkSize ?? ''}
                  onChange={(e) => setDraft({
                    ...draft,
                    maxTtsChunkSize: e.target.value === '' ? undefined : Number(e.target.value),
                  })}
                />
              </Field>
              <Field label={t('App.Admin.Booksellers.BooksellerEditDrawer.FieldAvailableVoicesJson')}>
                <textarea
                  className={`${textInput} font-mono text-xs`}
                  rows={6}
                  value={draft.availableVoicesJson ?? ''}
                  onChange={(e) => setDraft({ ...draft, availableVoicesJson: e.target.value || undefined })}
                />
              </Field>
            </div>
          )}
        </div>

        <footer className="border-t border-neutral-200 px-4 py-3 flex justify-between items-center">
          <button type="button" onClick={onClose} className="text-sm text-neutral-600 hover:underline">{t('App.Admin.Booksellers.BooksellerEditDrawer.Cancel')}</button>
          {(tab === 'basics' || tab === 'tts') && (
            <button
              type="button"
              onClick={() => (isNew ? create.mutate() : save.mutate())}
              disabled={!canSave || create.isPending || save.isPending || conflict}
              className="px-3 py-1 bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
            >
              {isNew ? t('App.Admin.Booksellers.BooksellerEditDrawer.Create') : t('App.Admin.Booksellers.BooksellerEditDrawer.Save')}
            </button>
          )}
        </footer>
      </aside>
    </div>
  );
}

const textInput = 'border border-neutral-300 rounded px-2 py-1 text-sm w-full';

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs text-neutral-600">{label}</span>
      {hint && <span className="text-xs text-neutral-400 ml-2">{hint}</span>}
      <div className="mt-1">{children}</div>
    </label>
  );
}
